import { useState } from 'react';
import authClient from '../client/authClient';
import { getPasswordResetRequestError } from '../utils/authUtils';
import { useCooldown } from './useCooldown';

/** Client-side throttle on "Resend" after each reset link that goes out. */
export const RESET_RESEND_COOLDOWN_SECONDS = 30;

interface UsePasswordResetOptions {
    email: string;
    /** Where the login page shows its one inline error ('' clears it). */
    setError: (message: string) => void;
    /** The first link went out: move to the "check your inbox" step. */
    onLinkSent: () => void;
}

/**
 * "Forgot password?" on the login page. The backend sends the email and
 * answers the same for any address, so success only means "the request was
 * accepted" and the next screen reads the same whether or not an account
 * exists. A failure keeps the person where they are with an honest sentence.
 */
export const usePasswordReset = ({ email, setError, onLinkSent }: UsePasswordResetOptions) => {
    const cooldown = useCooldown();
    const [sending, setSending] = useState(false);
    const [notice, setNotice] = useState('');

    /** Resolves to true when the link went out; shows the error otherwise. */
    const send = async (): Promise<boolean> => {
        setError('');
        setNotice('');
        setSending(true);
        try {
            await authClient.requestPasswordReset(email);
            cooldown.start(RESET_RESEND_COOLDOWN_SECONDS);
            return true;
        } catch (err: any) {
            setError(getPasswordResetRequestError(err?.response?.status));
            return false;
        } finally {
            setSending(false);
        }
    };

    const handleForgotPassword = async () => {
        if (sending) return;
        if (await send()) onLinkSent();
    };

    const handleResend = async () => {
        if (sending || cooldown.remaining > 0) return;
        if (await send()) setNotice('Sent again. Check your inbox.');
    };

    const clear = () => {
        cooldown.clear();
        setNotice('');
    };

    return {
        sending,
        resendCooldown: cooldown.remaining,
        notice,
        handleForgotPassword,
        handleResend,
        clear,
    };
};
